import React, { useState, useEffect } from 'react';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import { Table, Button, Tabs, Input, Select, Row, Col, Card } from 'antd'; // Removed Modal as it's not used here
import { useNavigate } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import moment from 'moment';
import slugs from '../../resources/slugs';
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import AdminLayout from "../../components/Layout";

const { TabPane } = Tabs;
const { Option } = Select;

const LoanPendingLoans = ({ from }) => { // Renamed component
  const [allPendingLoans, setAllPendingLoans] = useState([]);
  const [loanAdminPendingLoans, setLoanAdminPendingLoans] = useState([]);
  const [loanOfficerPendingLoans, setLoanOfficerPendingLoans] = useState([]);
  const [financeApproverPendingLoans, setFinanceApproverPendingLoans] = useState([]);
  const [financeDisbursementLoans, setFinanceDisbursementLoans] = useState([]); // This state is not used in this component, but was in original
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('loan_admin');
  const [search, setSearch] = useState('');
  const [type, setType] = useState(null);
  const navigate = useNavigate();

  const roles = from === 'admin' ? localStorage.getItem('roles') : JSON.parse(localStorage.getItem('roles'));

  const fetchPendingLoans = async () => {
    try {
      setLoading(true);
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      if (!token) {
        window.location.href = '/loan-admin-login';
        return;
      }

      let queryParams = '';
      if (search) queryParams += `search=${search}&`;
      if (type) queryParams += `type=${type}&`;
      if (queryParams) queryParams = `?${queryParams.slice(0, -1)}`;

      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/loans/pending${queryParams}`,
        null,
        false,
        false,
        token
      );
      if (res.status) {
        setAllPendingLoans(res.data);
      } else {
        NotificationManager.error(res.message || 'Failed to fetch pending loans');
      }
    } catch (error) {
      console.error('Error fetching pending loans:', error);
      NotificationManager.error('Error fetching pending loans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingLoans();
  }, [type]); // Re-fetch on type change automatically, search needs button or enter

  // getFinanceDisbursementLoans is not needed here, it was for a different tab/purpose

  const handleDownloadExcel = () => {
    try {
      const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const fileName = `Pending_Loans_${moment().format('YYYY-MM-DD_HH-mm')}`;

      const excelData = allPendingLoans.map(loan => ({
        'Loan ID': loan.id,
        'Customer Name': `${loan.first_name || ''} ${loan.last_name || ''}`,
        'Email': loan.email || 'N/A',
        'Phone': loan.phone || 'N/A',
        'Type': loan.type,
        'Amount': loan.amount,
        'Status': loan.status,
        'Created At': moment(loan.created_at).format('LLL'),
        'Loan Admin Status': loan.loan_admin_approval_status,
        'Loan Officer Status': loan.loan_officer_approval_status,
        'Finance Approver Status': loan.finance_approver_approval_status,
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = { Sheets: { 'Pending Loans': ws }, SheetNames: ['Pending Loans'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
      NotificationManager.success('Excel download started');
    } catch (error) {
      console.error('Excel download error:', error);
      NotificationManager.error('Failed to download Excel');
    }
  };

  useEffect(() => {
    const filterLoans = () => {
      const adminLoans = allPendingLoans.filter(loan => loan.loan_admin_approval_status === 'Pending');

      const officerLoans = allPendingLoans.filter(loan =>
        loan.loan_admin_approval_status === 'Approved' &&
        loan.loan_officer_approval_status === 'Pending'
      );

      const financeLoans = allPendingLoans.filter(loan =>
        loan.loan_admin_approval_status === 'Approved' &&
        loan.loan_officer_approval_status === 'Approved' &&
        loan.finance_approver_approval_status === 'Pending'
      );

      setLoanAdminPendingLoans(adminLoans);
      setLoanOfficerPendingLoans(officerLoans);
      setFinanceApproverPendingLoans(financeLoans);
    };

    filterLoans();
  }, [allPendingLoans]);

  const loanAdminColumns = [
    {
      title: 'Loan ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Customer Name',
      key: 'customer_name',
      render: (text, record) => `${record?.first_name || ''} ${record?.last_name || ''}`,
    },
    {
      title: 'Email',
      key: 'email',
      render: (text, record) => record?.email || 'N/A',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (text, record) => record?.phone || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Loan Admin Status',
      dataIndex: 'loan_admin_approval_status',
      key: 'loan_admin_approval_status',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => {
        const viewSlug = from === 'admin' ? slugs.loan_details_admin : slugs.loan_details;
        return (
          <Button type="link" onClick={() => navigate(viewSlug.replace(':id', record.id), { state: { from } })}>
            View More
          </Button>
        );
      },
    },
  ];

  const loanOfficerColumns = [
    {
      title: 'Loan ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Customer Name',
      key: 'customer_name',
      render: (text, record) => `${record?.first_name || ''} ${record?.last_name || ''}`,
    },
    {
      title: 'Email',
      key: 'email',
      render: (text, record) => record?.email || 'N/A',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (text, record) => record?.phone || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Loan Admin Status',
      dataIndex: 'loan_admin_approval_status',
      key: 'loan_admin_approval_status',
    },
    {
      title: 'Loan Officer Status',
      dataIndex: 'loan_officer_approval_status',
      key: 'loan_officer_approval_status',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => {
        const viewSlug = from === 'admin' ? slugs.loan_officer_details_admin : slugs.loan_officer_details;
        return (
          <Button type="link" onClick={() => navigate(viewSlug.replace(':id', record.id), { state: { from } })}>
            View More
          </Button>
        );
      },
    },
  ];

  const financeApproverColumns = [
    {
      title: 'Loan ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Customer Name',
      key: 'customer_name',
      render: (text, record) => `${record?.first_name || ''} ${record?.last_name || ''}`,
    },
    {
      title: 'Email',
      key: 'email',
      render: (text, record) => record?.email || 'N/A',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (text, record) => record?.phone || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Loan Admin Status',
      dataIndex: 'loan_admin_approval_status',
      key: 'loan_admin_approval_status',
    },
    {
      title: 'Loan Officer Status',
      dataIndex: 'loan_officer_approval_status',
      key: 'loan_officer_approval_status',
    },
    {
      title: 'Finance Approver Status',
      dataIndex: 'finance_approver_approval_status',
      key: 'finance_approver_approval_status',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => {
        const viewSlug = from === 'admin' ? slugs.finance_officer_details_admin : slugs.finance_officer_details;
        return (
          <Button type="link" onClick={() => navigate(viewSlug.replace(':id', record.id), { state: { from } })}>
            View More
          </Button>
        );
      },
    },
  ];

  if (loading) {
    return <div>Loading pending loans...</div>;
  }
  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;
  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <h1 style={{ marginBottom: 20 }}>Pending Loans</h1>

        <Card title="Filters" style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={12} md={8}>
              <p style={{ marginBottom: 8 }}>Search (Name, Email, Phone)</p>
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={fetchPendingLoans}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <p style={{ marginBottom: 8 }}>Loan Type</p>
              <Select
                placeholder="Select Type"
                style={{ width: '100%' }}
                value={type}
                onChange={(value) => setType(value)}
                allowClear
              >
                <Option value="Personal">Personal</Option>
                <Option value="Business">Business</Option>
                <Option value="POF">POF</Option>
              </Select>
            </Col>
            <Col xs={12} md={4}>
              <Button type="primary" onClick={fetchPendingLoans} block>
                Search
              </Button>
            </Col>
            <Col xs={12} md={4}>
              <Button onClick={() => { setSearch(''); setType(null); fetchPendingLoans(); }} block>
                Reset
              </Button>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Button
                type="dashed"
                onClick={handleDownloadExcel}
                icon={<DownloadOutlined />}
                disabled={allPendingLoans.length === 0}
              >
                Download as Excel
              </Button>
            </Col>
          </Row>
        </Card>

        <Tabs defaultActiveKey="loan_admin" activeKey={activeTab} onChange={setActiveTab}>
          {(roles?.includes('loan_admin') || roles.includes('OWNER') || roles.includes('SUPER_ADMIN')) &&
            <TabPane tab="Loan Admin Pending" key="loan_admin">
              <h2>Loan Admin Pending Loans</h2>
              {loanAdminPendingLoans.length === 0 ? (
                <p>No pending loans for Loan Admin.</p>
              ) : (
                <Table dataSource={loanAdminPendingLoans} columns={loanAdminColumns} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />
              )}
            </TabPane>
          }
          {(roles?.includes('loan_officer') || roles.includes('OWNER') || roles.includes('SUPER_ADMIN')) &&
            <TabPane tab="Loan Officer Pending" key="loan_officer">
              <h2>Loan Officer Pending Loans</h2>
              {loanOfficerPendingLoans.length === 0 ? (
                <p>No pending loans for Loan Officer.</p>
              ) : (
                <Table dataSource={loanOfficerPendingLoans} columns={loanOfficerColumns} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />
              )}
            </TabPane>
          }
          {(roles?.includes('finance_approver') || roles.includes('OWNER') || roles.includes('SUPER_ADMIN')) &&
            <TabPane tab="Finance Approver Pending" key="finance_approver">
              <h2>Finance Approver Pending Loans</h2>
              {financeApproverPendingLoans.length === 0 ? (
                <p>No pending loans for Finance Approver.</p>
              ) : (
                <Table dataSource={financeApproverPendingLoans} columns={financeApproverColumns} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />
              )}
            </TabPane>
          }
        </Tabs>
      </div>
    </Layout>
  );
};

export default LoanPendingLoans;
